import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { needsEducationPrompt } from '@backrs_ui/grow-ui/src/components/ProfileCompletionModal/criticalProfileFields';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useFetchProfileValueOptionsQuery,
  useFetchUserProfileInfoQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';
import { IS_SANDBOX } from '@backrs_ui/grow-ui/src/utils/sandboxSession';

/**
 * `blocking` is true while the prompt is outstanding or the profile that decides
 * it is still loading. `due` is narrower, turning true only once there is enough
 * loaded to render a filled-in form. A failed fetch clears both, so a hiccup
 * can't wedge the auto-modal chain shut.
 */
const useCriticalProfileGate = ({ enabled }: { enabled: boolean }) => {
  const { isGuest } = useAuth();
  const active = enabled && !isGuest && !IS_SANDBOX;

  const { data: profileData, isError: profileError } = useFetchUserProfileInfoQuery(
    USER_PROFILE_FIELDS,
    { skip: !active },
  );

  const profile = profileData?.profile;
  const graduated = profileData?.graduated ?? false;
  const incomplete = profile !== undefined && needsEducationPrompt(profile, graduated);

  // Only fetched once the profile says the prompt is due.
  const { data: profileOptions, isError: optionsError } = useFetchProfileValueOptionsQuery(
    undefined,
    { skip: !active || profileError || !incomplete },
  );

  const usable = active && !profileError && !optionsError;

  return {
    profile: usable ? profile : undefined,
    profileOptions: usable ? profileOptions : undefined,
    graduated: usable && graduated,
    due: usable && incomplete && profileOptions !== undefined,
    blocking: usable && (profile === undefined || incomplete),
  };
};

const AutoModalHoldContext = createContext(false);

export const AutoModalHoldProvider = AutoModalHoldContext.Provider;

/** Whether an earlier modal in the chain is still holding its turn. */
export const useAutoModalHold = () => useContext(AutoModalHoldContext);

/**
 * The prompt shows once per user per session. Dismissing it releases the hold on
 * the rest of the auto-modal chain -- it is retired for the session either way,
 * so holding the others back past that point would suppress them for nothing.
 */
export const useProfileCompletionModal = ({ enabled }: { enabled: boolean }) => {
  const [open, setOpen] = useState(false);
  const [dismissedFor, setDismissedFor] = useState<number | undefined>(undefined);
  const userId = useAppSelector(selectUserId);
  // Keyed per user, not per mount: the host can outlive a logout/login.
  const autoShownForRef = useRef<number | undefined>(undefined);
  const { profile, profileOptions, graduated, due, blocking } = useCriticalProfileGate({ enabled });

  useEffect(() => {
    // Logging out clears the user without unmounting, so drop the markers with them.
    if (!userId) {
      autoShownForRef.current = undefined;
      setDismissedFor(undefined);
      setOpen(false);

      return;
    }
    if (!due) {
      setOpen(false);

      return;
    }
    if (autoShownForRef.current === userId) return;

    autoShownForRef.current = userId;
    setOpen(true);
  }, [due, userId]);

  const onClose = useCallback(() => {
    setOpen(false);
    setDismissedFor(userId);
  }, [userId]);

  return {
    open,
    onClose,
    profile,
    profileOptions,
    graduated,
    blocking: blocking && dismissedFor !== userId,
  };
};
